import { SnakeMeta } from '@api';
import {
  ChangeYAxisSettingsProps,
  CustomMixDomain,
  intradayResamples,
  RangeHorizon,
  ResampleIntervals,
  TimeSeriesItem,
} from '@chart';
import { isSinglePriceEntity, isSnakeWithThreshold } from '@helpers';
import { useQueryClient } from '@tanstack/react-query';

import {
  createAssetEntitiesWithSnake,
  createChartPanesData,
  getInitialPrimaryAssetEntity,
  getValidHorizon,
  ValidChartSettings,
} from '../../utils/chart-settings-utils/chart-settings-utils';
import { createAllPanesChartAssetData } from '../../utils/chart-utils/chart-utils';
import { createChartDataItem } from '../../utils/chart-utils/createChartDataItem';
import type {
  TerminalChartAssetData,
  TerminalPaneData,
} from '../../utils/chart-utils/types';
import {
  getAssetResample,
  getChartDataEmptyResamples,
} from '../../utils/resample-utils/resample-utils';
import { ChartState } from '../use-chart-state/useChartState';
import { ThemeVariant, useChartTheme } from '../use-chart-theme/useChartTheme';
import {
  GetTimeSeriesProps,
  OnChartDataReadyProps,
} from '../use-turbo-chart/useTurboChart';
import {
  fetchChartEntities,
  fetchChartSnakeMeta,
} from './useChartSettings.utils';

export interface ChartDataResult {
  chartPanes: TerminalPaneData[];
  emptyResamples: ResampleIntervals[];
  validResample: ResampleIntervals;
  validHorizon: RangeHorizon | undefined;
  domain: CustomMixDomain | null;
  hidden?: string[];
  dailyTimeseries: TimeSeriesItem[];
}

export interface UseChartSettingsProps {
  onChartDataReady: (props: OnChartDataReadyProps) => void;
  getTimeSeries: (
    props: GetTimeSeriesProps
  ) => Promise<TimeSeriesItem[] | undefined>;
  chartPanes: TerminalPaneData[];
  updateChartState: (partial: Partial<ChartState>) => void;
  variants?: ThemeVariant[];
}

export const useChartSettings = ({
  onChartDataReady,
  getTimeSeries,
  chartPanes,
  updateChartState,
  variants = [],
}: UseChartSettingsProps) => {
  const { colors } = useChartTheme(variants);
  const queryClient = useQueryClient();

  const loadChartFromSettings = async (
    {
      assets,
      horizon,
      resample,
      hidden,
      domain,
      paneSettings,
      primarySnake,
      episodes,
    }: ValidChartSettings,
    signal?: AbortSignal
  ): Promise<ChartDataResult> => {
    const tags = assets.map(asset => asset.tag);
    const [entities, snakeMetas] = await Promise.all([
      fetchChartEntities({
        tags,
        queryClient,
      }),
      Promise.all(
        assets.map(asset =>
          fetchChartSnakeMeta({ snake: asset.snake, queryClient })
        )
      ),
    ]);

    const { assetEntities, paneStartIndexes } = createAssetEntitiesWithSnake({
      assets,
      entities,
      snakeMetas,
    });
    const { primaryAssetIndex, primaryPaneIndex } =
      getInitialPrimaryAssetEntity({
        assets,
        primarySnake,
        assetEntities,
      });
    const primaryEntity = assetEntities[primaryAssetIndex];
    const hasSinglePriceEntity = assetEntities.some(entity =>
      isSinglePriceEntity(entity)
    );
    const initialValidResample = getAssetResample({
      asset: primaryEntity,
      selectedResample: resample,
      hasSinglePriceEntity,
    });
    const shouldFetchDaily = initialValidResample !== ResampleIntervals.OneDay;

    // If the primary asset's intraday request returns no data, `getTimeSeries`
    // transparently falls back to daily prices. When that happens we have to
    // realign the rest of the chart with the data we actually got: drop to
    // daily resample, force horizon to the default (3M), and disable the
    // intraday affordances (1D horizon button, intraday resamples)
    const fallbackFlags = assetEntities.map(() => false);

    const [dailyTs, currentResamplePrices] = await Promise.all([
      shouldFetchDaily
        ? getTimeSeries({
            latestAsset: primaryEntity,
            resolution: ResampleIntervals.OneDay,
          })
        : Promise.resolve([]),
      Promise.all(
        assetEntities.map((asset, index) =>
          getTimeSeries({
            latestAsset: asset,
            resolution: initialValidResample,
            onFallbackToDaily: () => {
              fallbackFlags[index] = true;
            },
          })
        )
      ),
    ]);

    const primaryFellBackToDaily = fallbackFlags[primaryAssetIndex];

    const validResample = primaryFellBackToDaily
      ? ResampleIntervals.OneDay
      : initialValidResample;

    const dailyTimeseries =
      (shouldFetchDaily ? dailyTs : currentResamplePrices[primaryAssetIndex]) ??
      [];

    const primaryResamplePrice = currentResamplePrices[primaryAssetIndex] ?? [];
    const hasNoPrices = (
      prices: Array<TimeSeriesItem[] | undefined>
    ): prices is Array<TimeSeriesItem[]> => !prices.some(item => item?.length);

    if (
      !dailyTimeseries.length &&
      hasNoPrices(currentResamplePrices) &&
      !primaryResamplePrice.length
    ) {
      throw new Error('Some data is missing');
    }

    // If the checks has passed
    // then we create panes from assetEntities + currentResamplePrices + existingSnakeMetas
    // NOTE: THE ORDER MATTERS!!! assetEntities may not contain same entities
    // as in the assets as there can be case when we coudn't fetch snakeMeta or ts is empty
    // SO currentResamplePrices and existingSnakeMetas are based on the assetEntities which are filtered
    const existingSnakeMetas = snakeMetas.filter(Boolean) as SnakeMeta[];
    const primaryChartData = createChartDataItem({
      entity: primaryEntity,
      ts: primaryResamplePrice,
      lineColorToken: isSnakeWithThreshold(
        existingSnakeMetas[primaryAssetIndex]
      )
        ? colors.default.token
        : colors.getMulti(assets.length)[primaryAssetIndex].token,
      isHidden: hidden?.includes(primaryEntity.default_snake),
      snakeMeta: existingSnakeMetas[primaryAssetIndex],
      episodes,
    });
    const allPanesChartAssetData: TerminalChartAssetData[] =
      createAllPanesChartAssetData({
        assetEntities,
        primaryEntity,
        primaryChartData,
        currentResamplePrices,
        existingSnakeMetas,
        colors,
        hidden,
        episodes,
      });
    const chartPanes: TerminalPaneData[] = createChartPanesData({
      chartAssetData: allPanesChartAssetData,
      paneStartIndexes,
      paneSettings,
      primaryPaneIndex,
      primaryAssetIndex,
    });

    // Same union the add/remove-asset paths use, so a non-subscribable
    // secondary asset disables intraday on load too — not only after the
    // next asset change.
    const baseEmptyResamples = getChartDataEmptyResamples(
      allPanesChartAssetData
    );
    const emptyResamples = primaryFellBackToDaily
      ? Array.from(new Set([...baseEmptyResamples, ...intradayResamples]))
      : baseEmptyResamples;
    const validHorizon = getValidHorizon({
      horizon,
      chartData: allPanesChartAssetData,
      emptyResamples,
      hasSinglePriceEntity,
      resample,
      fellBackToDaily: primaryFellBackToDaily,
    });

    signal?.throwIfAborted();
    onChartDataReady({
      chartPanes,
      emptyResamples,
      validResample,
      validHorizon,
      domain,
      hidden,
      dailyTimeseries,
    });

    return {
      chartPanes,
      emptyResamples,
      validResample,
      validHorizon,
      domain,
      hidden,
      dailyTimeseries,
    };
  };

  const changeYAxisSettings = ({ type, paneId }: ChangeYAxisSettingsProps) => {
    const newChartPanes = chartPanes.map(pane => {
      if (pane.id === paneId) {
        return {
          ...pane,
          yAxisType: type ?? pane.yAxisType,
        };
      }

      return pane;
    });

    updateChartState({
      chartPanes: newChartPanes,
    });
  };

  return {
    loadChartFromSettings,
    changeYAxisSettings,
  };
};
